import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '@/utils/api';
import { nomeNoAvisoLigado } from './nomeNoAviso';

/**
 * O PUSH NESTE APARELHO (1.4; 1.4.1 com várias contas e "Avisos neste celular") — o que a tela pede ao plugin e ao servidor.
 * O servidor é `backend/src/services/push.ts`; a pergunta e o toque, `PushNoAplicativo.tsx`.
 *
 * - SÓ NO APP 1.4.1 OU MAIS NOVO (`BUILD_COM_PUSH`): o push depende do nativo novo (o Firebase no Android, a capacidade de
 *   push no iPhone). No app antigo a tela nem pergunta nem chama o plugin — o pacote de telas é o mesmo para os dois, e o
 *   nativo mínimo NÃO sobe (quem fica no app antigo continua recebendo as telas, com o sino e sem push).
 * - O TOKEN fica guardado aqui (`push:token`) para "Sair" e "Sair desta conta" tirarem o aparelho do servidor.
 * - "AVISOS NESTE CELULAR" (`push:desligado:<conta>`): desligado, este aparelho sai do push daquela conta, e o sino continua.
 */
/** O build nativo em que o push existe: Android versionCode 6; iOS, o build seguinte ao 75 (a 1.4, etiqueta app-1.4.0-r4) — o Archive soma 1 sozinho. */
export const BUILD_COM_PUSH = { android: 6, ios: 76 } as const;

const TOKEN = 'push:token';
const DESLIGADO = (id: string | number) => `push:desligado:${id}`;

/** No build de TESTE da web (o roteiro das telas), o plugin e o "app" podem ser de mentira — nunca no de produção. */
const deTeste = import.meta.env.VITE_COFRE_DE_TESTE === '1';
type Plugin = typeof PushNotifications;
export const plugin = (): Plugin => (deTeste && (window as unknown as { __pushDeTeste?: Plugin }).__pushDeTeste) || PushNotifications;
const infoDoApp = async (): Promise<{ version: string; build: string; id?: string } | null> => {
  const falso = deTeste ? (window as unknown as { __appInfoDeTeste?: { version: string; build: string } }).__appInfoDeTeste : undefined;
  if (falso) return falso;
  return App.getInfo().catch(() => null);
};

/** Este app tem o nativo do push? (No navegador, não.) */
export async function nativoComPush(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  const info = await infoDoApp();
  // O APK de teste (…app.teste, app 1.5) não tem o Firebase configurado para o pacote dele: sem push, nunca registra.
  if (info?.id?.endsWith('.teste')) return false;
  const build = Number(info?.build);
  const minimo = Capacitor.getPlatform() === 'ios' ? BUILD_COM_PUSH.ios : BUILD_COM_PUSH.android;
  return Number.isFinite(build) && build >= minimo;
}

/**
 * O push está ligado no servidor? A pergunta leva `geracao=2`: sem ele o servidor responde sempre "desligado" — é o que
 * impede as telas da 1.4 (que não conferem o build) de perguntar (`notificacoesRoutes.ts`, `GET /push`).
 */
export async function pushLigadoNoServidor(): Promise<boolean> {
  return api.get<{ ligado: boolean }>('/api/notificacoes/push', { params: { geracao: 2 } }).then((r) => !!r.data.ligado, () => false);
}

export function tokenGuardado(): string | null { try { return localStorage.getItem(TOKEN); } catch { return null; } }
export function avisosNesteCelular(userId: string | number): boolean { try { return localStorage.getItem(DESLIGADO(userId)) !== '1'; } catch { return true; } }

/** Manda (de novo) o token ao servidor, com as contas que estão neste aparelho e a opção "mostrar o nome". */
export async function registrarAparelhoNoPush(userId: string | number, token?: string): Promise<void> {
  let t = token ?? null;
  try { t = t ?? localStorage.getItem(TOKEN); if (t) localStorage.setItem(TOKEN, t); } catch { /* sem guardar: o servidor apaga quando o Google ou a Apple recusarem */ }
  if (!t || !avisosNesteCelular(userId)) return;
  const info = await infoDoApp();
  const { listarContas } = await import('@/features/auth/contas/contasDoAparelho');
  const contasNoAparelho = (await listarContas().catch(() => [])).map((c) => c.userId);
  await api.post('/api/notificacoes/aparelhos', { plataforma: Capacitor.getPlatform(), token: t, versaoApp: info ? `${info.version} (${info.build})` : null, mostrarNome: nomeNoAvisoLigado(userId), contasNoAparelho }).catch(() => undefined);
}

/** "Sair": este aparelho deixa de receber os avisos da conta aberta. O token fica para as outras contas do aparelho. */
export async function removerAparelhoDoPush(): Promise<void> {
  const t = tokenGuardado();
  if (t) await api.delete('/api/notificacoes/aparelhos', { data: { token: t } }).catch(() => undefined);
}
/** "Sair desta conta" (Trocar de conta), ou o aviso de uma conta que saiu do aparelho: tira só aquela conta. */
export async function removerContaDoPush(userId: number): Promise<void> {
  const t = tokenGuardado();
  if (t) await api.delete('/api/notificacoes/aparelhos', { data: { token: t, contas: [userId] } }).catch(() => undefined);
}

/** "Avisos neste celular": desligar tira este aparelho do push desta conta; ligar registra de novo (pede a permissão, se faltar). */
export async function mudarAvisosNesteCelular(userId: string | number, ligar: boolean): Promise<boolean> {
  try { if (ligar) localStorage.removeItem(DESLIGADO(userId)); else localStorage.setItem(DESLIGADO(userId), '1'); } catch { /* fica só nesta tela */ }
  if (!ligar) { await removerAparelhoDoPush(); return false; }
  const p = await plugin().checkPermissions().catch(() => null);
  const r = p?.receive === 'granted' ? p : await plugin().requestPermissions().catch(() => null);
  if (r?.receive !== 'granted') return false;
  await plugin().register().catch(() => undefined);
  await registrarAparelhoNoPush(userId);
  return true;
}
